

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useCallbackRef } from './use-callback-ref';

/**
 * This code is copied and modified from the following source:
 * Author: WorkOS https://workos.com/
 * Source: https://github.com/radix-ui/primitives/blob/main/packages/react/use-controllable-state/src/useControllableState.tsx
 * License: MIT https://github.com/radix-ui/primitives/blob/main/LICENSE
 */

interface UseControllableStateParamsInterface<T> {
  prop?: T | undefined;
  defaultProp?: T | undefined;
  onChange?: (state: T) => void;
}

type SetStateFn<T> = (prevState?: T) => T;

/**
 * Hook that seamlessly merges controlled and uncontrolled state patterns.
 * Automatically switches between controlled mode (when prop is provided) and uncontrolled mode
 * (when prop is undefined). Essential for building flexible component APIs that support both patterns.
 *
 * @template T - The type of the state value.
 * @param {object} params - Configuration object for controllable state.
 * @param {T} [params.prop] - The controlled value (when provided, component is controlled).
 * @param {T} [params.defaultProp] - The default value for uncontrolled mode.
 * @param {function} [params.onChange] - Callback function called when state changes.
 * @returns {[T | undefined, Dispatch<SetStateAction<T | undefined>>]} Tuple containing current value and setter function.
 *
 * @example
 * const { value, defaultValue, onChange } = props;
 * const [valueState, setValueState] = useControllableState({
 *    prop: value,
 *    defaultProp: defaultValue,
 *    onChange: onChange,
 *  });
 * return <input value={valueState} onChange={(e) => setValueState(e.currentTarget.value)} />
 */
export function useControllableState<T>({
  prop,
  defaultProp,
  onChange = () => {},
}: UseControllableStateParamsInterface<T>) {
  const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({ defaultProp, onChange });
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledProp;
  const handleChange = useCallbackRef(onChange);

  const setValue: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (nextValue) => {
      if (isControlled) {
        const setter = nextValue as SetStateFn<T>;
        const value = typeof nextValue === 'function' ? setter(prop) : nextValue;
        if (value !== prop) handleChange(value as T);
      } else {
        setUncontrolledProp(nextValue);
      }
    },
    [isControlled, prop, setUncontrolledProp, handleChange],
  );

  return [value, setValue] as const;
}

function useUncontrolledState<T>({
  defaultProp,
  onChange,
}: Omit<UseControllableStateParamsInterface<T>, 'prop'>) {
  const uncontrolledState = useState<T | undefined>(defaultProp);
  const [value] = uncontrolledState;
  const prevValueRef = useRef(value);
  const handleChange = useCallbackRef(onChange);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      handleChange(value as T);
      prevValueRef.current = value;
    }
  }, [value, prevValueRef, handleChange]);

  return uncontrolledState;
}
